import * as signalR from "@microsoft/signalr";

export type ConnectionState = "Disconnected" | "Connecting" | "Connected" | "Reconnecting";

/**
 * Politique de reconnexion avec backoff exponentiel + jitter.
 * Délais générés : ~1s, ~2s, ~4s, ~8s, ~16s, 30s (plafond).
 * Utilisée par withAutomaticReconnect pour les coupures après connexion établie.
 */
class ExponentialBackoffRetryPolicy implements signalR.IRetryPolicy {
    nextRetryDelayInMilliseconds(retryContext: signalR.RetryContext): number | null {
        const base = Math.min(1_000 * Math.pow(2, retryContext.previousRetryCount), 30_000);
        return base + Math.floor(Math.random() * 1_000);
    }
}

/**
 * Gestionnaire de connexion SignalR O2SHub.
 *
 * Fonctionnalités :
 * - États explicites : Disconnected | Connecting | Connected | Reconnecting
 * - Backoff exponentiel pour la reconnexion automatique (SDK) ET les erreurs de connexion initiale
 * - Watchdog applicatif : ping hub toutes les 45 s, reconnexion forcée si le hub ne répond pas
 * - Reconnexion automatique au retour sur l'onglet (visibilitychange)
 * - Nettoyage propre via dispose() ou beforeunload
 */
class SignalRManager {
    public connection: signalR.HubConnection;
    public id: string = "";
    public isInitialize: boolean = false;

    private _state: ConnectionState = "Disconnected";
    private _stateListeners: Array<(state: ConnectionState) => void> = [];
    private _watchdogTimer: ReturnType<typeof setInterval> | null = null;
    private _reconnectAttempt = 0;

    /** Intervalle du watchdog applicatif (ping hub). */
    private readonly WATCHDOG_INTERVAL = 45_000;

    public get state(): ConnectionState {
        return this._state;
    }

    public constructor(url: string) {
        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(url, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect(new ExponentialBackoffRetryPolicy())
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        // Timeouts SDK :
        //   - keepAlive : le client envoie un ping WebSocket toutes les 15 s
        //   - serverTimeout : si aucun message reçu pendant 30 s → connexion considérée perdue
        this.connection.keepAliveIntervalInMilliseconds = 15_000;
        this.connection.serverTimeoutInMilliseconds = 30_000;

        this.connection.onreconnecting((error) => {
            console.warn("SignalR O2SHub : reconnexion en cours...", error ?? "");
            this._setState("Reconnecting");
            this._stopWatchdog();
        });

        this.connection.onreconnected(async () => {
            console.log("SignalR O2SHub : reconnecté");
            this._reconnectAttempt = 0;
            await this.connection.invoke("AssociateWeb", this.id);
            this._setState("Connected");
            this._startWatchdog();
        });

        this.connection.onclose((error) => {
            // Déclenché quand withAutomaticReconnect a épuisé toutes ses tentatives,
            // ou lors d'un arrêt volontaire (dispose / stop).
            console.warn("SignalR O2SHub : connexion fermée", error ?? "");
            this._setState("Disconnected");
            this._stopWatchdog();
            // Le watchdog redémarrera la connexion si isInitialize est toujours true.
        });

        // Fermeture de l'onglet : nettoyage best-effort (navigateur n'attend pas les Promises)
        window.addEventListener("beforeunload", this._onBeforeUnload);

        // Retour sur l'onglet après suspension : forcer une reconnexion si nécessaire
        window.addEventListener("visibilitychange", this._onVisibilityChange);
    }

    // ─── Gestionnaires d'événements page ─────────────────────────────────────

    private _onBeforeUnload = (): void => {
        if (!this.isInitialize) return;
        this._stopWatchdog();
        // Best-effort : synchrone, le navigateur ne garantit pas l'exécution des Promises
        try { this.connection.stop(); } catch { /* ignore */ }
    };

    private _onVisibilityChange = (): void => {
        if (!this.isInitialize) return;
        if (document.visibilityState === "visible") {
            // L'onglet revient au premier plan : relancer si la connexion est tombée
            if (this.connection.state === signalR.HubConnectionState.Disconnected) {
                console.log("SignalR O2SHub : onglet redevenu actif, reconnexion...");
                this.start();
            } else if (this.connection.state === signalR.HubConnectionState.Connected) {
                // Relancer le watchdog s'il avait été arrêté (onglet en arrière-plan)
                this._startWatchdog();
            }
        } else {
            // Onglet masqué : suspendre le watchdog pour économiser les ressources
            this._stopWatchdog();
        }
    };

    // ─── Gestion de l'état ────────────────────────────────────────────────────

    private _setState(state: ConnectionState): void {
        this._state = state;
        this._stateListeners.forEach(fn => fn(state));
    }

    /**
     * Abonnement aux changements d'état de la connexion.
     * @returns Fonction de désabonnement.
     */
    public onStateChange(listener: (state: ConnectionState) => void): () => void {
        this._stateListeners.push(listener);
        return () => {
            this._stateListeners = this._stateListeners.filter(fn => fn !== listener);
        };
    }

    // ─── Watchdog applicatif ─────────────────────────────────────────────────

    private _startWatchdog(): void {
        this._stopWatchdog();
        this._watchdogTimer = setInterval(async () => {
            if (!this.isInitialize) return;

            // Si déjà en cours de reconnexion (SDK), ne pas interférer
            const sdkState = this.connection.state;
            if (sdkState === signalR.HubConnectionState.Reconnecting ||
                sdkState === signalR.HubConnectionState.Connecting) {
                return;
            }

            // Connexion perdue (SDK a épuisé ses tentatives) → relancer manuellement
            if (sdkState === signalR.HubConnectionState.Disconnected) {
                console.warn("SignalR O2SHub : watchdog — connexion perdue, reconnexion...");
                this.start();
                return;
            }

            // Connexion apparemment active → ping applicatif pour confirmer la réactivité du hub
            try {
                await this.connection.invoke("Ping");
            } catch (err) {
                console.warn("SignalR O2SHub : watchdog ping échoué, reconnexion forcée...", err);
                this._stopWatchdog();
                this.start();
            }
        }, this.WATCHDOG_INTERVAL);
    }

    private _stopWatchdog(): void {
        if (this._watchdogTimer !== null) {
            clearInterval(this._watchdogTimer);
            this._watchdogTimer = null;
        }
    }

    // ─── API publique ─────────────────────────────────────────────────────────

    public isConnected(): boolean {
        return this.connection.state === signalR.HubConnectionState.Connected;
    }

    /**
     * Initialise le manager avec l'identifiant utilisateur.
     * Doit être appelé avant start().
     */
    public initialize(id: string, closeCallback?: (error?: Error) => void): void {
        this.id = id;
        this.isInitialize = true;

        if (closeCallback) {
            this.connection.onclose(closeCallback);
        }

        this.connection.on("WebAssociate", (email: string) => {
            console.log(`SignalR client associated for user ${email} on O2SHub`);
            if (Notification.permission === "granted") {
                new Notification(`SignalR client associated for user ${email} on O2SHub`);
            }
        });
    }

    /**
     * Démarre la connexion. Gère le backoff exponentiel en cas d'échec initial.
     */
    public async start(): Promise<void> {
        const sdkState = this.connection.state;
        if (sdkState === signalR.HubConnectionState.Connected ||
            sdkState === signalR.HubConnectionState.Connecting ||
            sdkState === signalR.HubConnectionState.Reconnecting) {
            return;
        }

        this._setState("Connecting");
        const attempt = this._reconnectAttempt + 1;
        console.log(`SignalR O2SHub : démarrage de la connexion (tentative ${attempt})...`);

        try {
            await this.connection.start();
            await this.connection.invoke("AssociateWeb", this.id);
            this._reconnectAttempt = 0;
            this._setState("Connected");
            this._startWatchdog();
            console.log("SignalR O2SHub : connecté");
        } catch (error) {
            console.error("SignalR O2SHub : erreur de connexion", error);
            this._setState("Disconnected");

            // Backoff exponentiel pour les échecs de connexion initiale
            const delay = Math.min(1_000 * Math.pow(2, this._reconnectAttempt), 30_000)
                + Math.floor(Math.random() * 1_000);
            this._reconnectAttempt++;
            console.warn(`SignalR O2SHub : nouvelle tentative dans ${Math.round(delay / 1_000)} s...`);

            setTimeout(() => {
                if (this.isInitialize) this.start();
            }, delay);
        }
    }

    /**
     * Arrête proprement la connexion et libère toutes les ressources.
     * À appeler lors de la déconnexion utilisateur.
     */
    public async dispose(): Promise<void> {
        this.isInitialize = false;
        this._stopWatchdog();
        this._stateListeners = [];
        this.connection.off("WebAssociate");

        try {
            await this.connection.stop();
        } catch (err) {
            console.warn("SignalR O2SHub : erreur lors de l'arrêt", err);
        }
    }
}

export default new SignalRManager(import.meta.env.VITE_API_CENTRE_URL + import.meta.env.VITE_SIGNALR_URL);
